import { useFormContext, useFieldArray } from 'react-hook-form';
import { useEffect } from 'react';

interface UseDynamicMatrixProps {
    fieldName: string;
    watchFieldName: string;
    labelPrefix: string;
}

export const useDynamicQuestionMatrix = ({
    fieldName,
    watchFieldName,
    labelPrefix,
}: UseDynamicMatrixProps) => {
    const { control, watch, setValue } = useFormContext();
    const { fields, remove, insert, append, replace } = useFieldArray({
        control,
        name: fieldName,
    });

    const fieldCount = watch(watchFieldName);

    useEffect(() => {
        if (fieldCount === fields.length) return;

        const newFields = Array.from({ length: fieldCount }, (_, i) => ({
            id: null,
            label: `${labelPrefix} ${i + 1}`,
        }));

        replace(newFields);
    }, [fieldCount, replace, labelPrefix, fields.length]);

    const removeField = (fieldIndex: number) => {
        remove(fieldIndex);
        setValue(watchFieldName, fieldCount ? fieldCount - 1 : 0);
    };

    const insertField = (fieldIndex: number) => {
        insert(fieldIndex + 1, {
            id: null,
            label: `${labelPrefix} ${fieldCount + 1}`,
        });
        setValue(watchFieldName, fieldCount + 1);
    };

    const appendFields = (labels: string[]) => {
        if (!labels || !labels.length) return;
        labels.forEach((label) => {
            append({
                id: null,
                label: label,
            });
        });
        setValue(watchFieldName, fieldCount + labels.length);
    };

    return {
        fields,
        removeField,
        insertField,
        appendFields,
    };
};
